import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/lab/seances.css";

/**
 * Planning des séances d'une session (maquettes de l'équipe, points 4.1/4.2).
 *
 *  - « Jours fixes (récurrent) » : on coche des jours de la semaine, chacun
 *    avec ses heures ; les séances sont générées entre dateDebut et dateFin.
 *  - « Dates personnalisées » : la liste des séances, une par une.
 *
 * Contrôlé : `seances` ([{date, heure_debut, heure_fin}]) et `onChange`.
 * Le serveur vérifie tout (cohérence, chevauchements, conflits de salle) ;
 * ici on aide seulement la saisie.
 */

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const HEURES_DEFAUT = { debut: "08:30", fin: "10:30" };
const SEANCES_MAX = 400;

const pad2 = (n) => String(n).padStart(2, "0");
const hm = (h) => (h || "").slice(0, 5);                       // "09:00:00" → "09:00"
const enMinutes = (h) => { const [a, b] = hm(h).split(":").map(Number); return a * 60 + (b || 0); };
const versDate = (iso) => { const [a, m, j] = iso.split("-").map(Number); return new Date(a, m - 1, j); };
const versIso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const indexJour = (d) => (d.getDay() + 6) % 7;                  // lundi = 0
const dateValide = (iso) => /^\d{4}-\d{2}-\d{2}$/.test(iso || "");

export const dureeSeance = (s) => Math.max(enMinutes(s.heure_fin) - enMinutes(s.heure_debut), 0);

export const formaterVolume = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${pad2(m)}` : `${h} h`;
};

/** Séances générées par les jours cochés entre deux dates (incluses). */
export const genererSeances = (dateDebut, dateFin, jours) => {
  if (!dateValide(dateDebut) || !dateValide(dateFin) || dateDebut > dateFin) return [];
  const out = [];
  for (let d = versDate(dateDebut); versIso(d) <= dateFin && out.length < SEANCES_MAX;
       d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    const j = jours[indexJour(d)];
    if (j.actif) out.push({ date: versIso(d), heure_debut: j.debut, heure_fin: j.fin });
  }
  return out;
};

/** Configuration « jours fixes » déduite de séances existantes. */
const joursDepuis = (seances) => {
  const jours = JOURS.map(() => ({ actif: false, ...HEURES_DEFAUT }));
  seances.forEach((s) => {
    if (!dateValide(s.date)) return;
    const j = jours[indexJour(versDate(s.date))];
    if (!j.actif) Object.assign(j, { actif: true, debut: hm(s.heure_debut), fin: hm(s.heure_fin) });
  });
  return jours;
};

const memeListe = (a, b) =>
  a.length === b.length && a.every((s, i) =>
    s.date === b[i].date && hm(s.heure_debut) === hm(b[i].heure_debut) && hm(s.heure_fin) === hm(b[i].heure_fin));

export default function SeancesEditor({ dateDebut, dateFin, seances, onChange, erreur }) {
  const liste = useMemo(() => seances || [], [seances]);
  // Une session qui a déjà des séances s'ouvre sur sa liste ; une nouvelle, sur les jours fixes.
  const [mode, setMode] = useState(() => (liste.length ? "personnalise" : "recurrent"));
  const [jours, setJours] = useState(() => joursDepuis(liste));
  const derniereEmise = useRef(liste);

  // En mode récurrent, la liste découle des jours cochés et de la période.
  useEffect(() => {
    if (mode !== "recurrent") return;
    const generees = genererSeances(dateDebut, dateFin, jours);
    if (!memeListe(generees, derniereEmise.current)) {
      derniereEmise.current = generees;
      onChange(generees);
    }
  }, [mode, dateDebut, dateFin, jours, onChange]);

  const emettre = (nouvelles) => { derniereEmise.current = nouvelles; onChange(nouvelles); };

  const majJour = (i, champ, valeur) =>
    setJours((js) => js.map((j, k) => (k === i ? { ...j, [champ]: valeur } : j)));

  const majSeance = (i, champ, valeur) =>
    emettre(liste.map((s, k) => (k === i ? { ...s, [champ]: valeur } : s)));

  const ajouterSeance = () => {
    const derniere = liste[liste.length - 1];
    let date = dateValide(dateDebut) ? dateDebut : versIso(new Date());
    if (derniere && dateValide(derniere.date)) {
      const d = versDate(derniere.date);
      date = versIso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
    }
    emettre([...liste, {
      date,
      heure_debut: derniere ? hm(derniere.heure_debut) : HEURES_DEFAUT.debut,
      heure_fin: derniere ? hm(derniere.heure_fin) : HEURES_DEFAUT.fin,
    }]);
  };

  const passerEnRecurrent = () => {
    setJours(joursDepuis(liste.length ? liste : []));
    setMode("recurrent");
  };

  const volume = liste.reduce((t, s) => t + dureeSeance(s), 0);

  return (
    <div className="seances-editeur">
      <div className="seances-titre"><i className="fa-regular fa-calendar"></i> Planning des séances</div>

      <div className="seances-modes" role="tablist">
        <button type="button" className={mode === "recurrent" ? "actif" : ""} onClick={passerEnRecurrent}>
          Jours fixes (récurrent)
        </button>
        <button type="button" className={mode === "personnalise" ? "actif" : ""}
                onClick={() => setMode("personnalise")}>
          Dates personnalisées
        </button>
      </div>

      {mode === "recurrent" ? (
        <>
          {(!dateValide(dateDebut) || !dateValide(dateFin)) && (
            <p className="seances-aide">Renseignez la date de début et la date de fin : les séances sont générées entre les deux.</p>
          )}
          <div className="seances-jours">
            {JOURS.map((nom, i) => {
              const j = jours[i];
              const invalide = j.actif && enMinutes(j.fin) <= enMinutes(j.debut);
              return (
                <div key={nom} className={`seances-ligne ${j.actif ? "" : "inactive"}`}>
                  <label className="seances-jour">
                    <input type="checkbox" checked={j.actif} onChange={(e) => majJour(i, "actif", e.target.checked)} />
                    {nom}
                  </label>
                  <input type="time" value={j.debut} className={invalide ? "invalide" : ""}
                         onChange={(e) => majJour(i, "debut", e.target.value)} />
                  <span className="seances-a">à</span>
                  <input type="time" value={j.fin} className={invalide ? "invalide" : ""}
                         onChange={(e) => majJour(i, "fin", e.target.value)} />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="seances-liste">
          {!liste.length && <p className="seances-aide">Aucune séance : ajoutez-en une.</p>}
          {liste.map((s, i) => {
            const invalide = enMinutes(s.heure_fin) <= enMinutes(s.heure_debut);
            return (
              <div key={i} className="seances-ligne">
                <input type="date" value={s.date} className="seances-date"
                       onChange={(e) => majSeance(i, "date", e.target.value)} />
                <input type="time" value={hm(s.heure_debut)} className={invalide ? "invalide" : ""}
                       onChange={(e) => majSeance(i, "heure_debut", e.target.value)} />
                <span className="seances-a">à</span>
                <input type="time" value={hm(s.heure_fin)} className={invalide ? "invalide" : ""}
                       onChange={(e) => majSeance(i, "heure_fin", e.target.value)} />
                <button type="button" className="seances-supprimer" title="Supprimer la séance"
                        onClick={() => emettre(liste.filter((_, k) => k !== i))}>
                  <i className="fa-regular fa-trash-can"></i>
                </button>
              </div>
            );
          })}
          <button type="button" className="seances-ajouter" onClick={ajouterSeance}>
            <i className="fa-solid fa-plus"></i> Ajouter une séance
          </button>
        </div>
      )}

      <div className="seances-resume">
        <div><span>Nombre de séances</span><strong>{liste.length}</strong></div>
        <div><span>Volume horaire total</span><strong>{formaterVolume(volume)}</strong></div>
      </div>

      {erreur && <div className="seances-erreur">{erreur}</div>}
    </div>
  );
}
